import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { dashboardService, announcementService } from '../../services';
import { staffReportsService } from '../../services/carehome_manager';
import { Modal, Spinner } from '../../components';
import RemindersCard from './RemindersCard';
import { DashboardSkeleton } from '../../skeletons';
import {
  Users, Clock, Megaphone, Calendar, Plus, ArrowRight, MapPin, UserCheck, BedDouble,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { localTime } from '../../utils/datetime';
// Reuse the schedule screen's own review connector so the dashboard's "to
// review" counts are identical to the schedule by construction.
import { visitReviewKind } from './domiciliary_roster/scheduleUtils';

/**
 * Manager Dashboard — premium visual redesign (matches the approved concept).
 * Same content & data as before (single /dashboard call):
 *  - Page header (eyebrow + title)
 *  - Three stat cards, full width — domiciliary: Clients / Staff / Today's
 *    Visits; care home: Residents / Staff on Duty / Occupancy
 *  - Split: "Care Packages" (domiciliary) / "Schedule" (care-home), each with a
 *    Today / Tomorrow toggle (Today default; tomorrow fetched on demand)
 *    on the left, rail (Reminders / Announcements / Upcoming Events) on the right
 * Only presentation changed — logic, handlers and endpoints are unchanged.
 */

// Shared card shell — white surface, 20px radius, soft border + layered shadow.
const CARD =
  'bg-white dark:bg-[#262b33] rounded-[20px] border border-[#E3E9F3] dark:border-gray-800 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_4px_10px_-3px_rgba(15,23,42,0.06)]';

// Gradient "spine" that opens every section header.
const Spine = ({ from = 'from-blue-600', to = 'to-indigo-600' }) => (
  <span className={`w-1 h-[18px] rounded bg-gradient-to-b ${from} ${to}`} />
);

const initials = (name) => {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
};

const StatusPill = ({ status }) => {
  const map = {
    completed: { label: '✓ Done', cls: 'bg-[#ECFDF5] dark:bg-emerald-900/10 text-[#059669] dark:text-emerald-400 border-[#CFF3E4] dark:border-emerald-900/50' },
    in_progress: { label: 'In Progress', cls: 'bg-[#FFFBEB] dark:bg-amber-900/10 text-[#D97706] dark:text-amber-400 border-[#FBE3C0] dark:border-amber-900/50' },
    pending: { label: 'Scheduled', cls: 'bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400 border-[#D8E4FF] dark:border-blue-900/50' },
    missed: { label: 'Missed', cls: 'bg-[#FFF1F3] dark:bg-red-900/10 text-[#E11D48] dark:text-red-400 border-[#FFDCE3] dark:border-red-900/50' },
    open: { label: 'Open', cls: 'bg-[#F4F7FC] dark:bg-[#31373f] text-[#64748B] dark:text-gray-400 border-[#E3E9F3] dark:border-gray-800' },
  };
  const cfg = map[status] || map.pending;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-lg border ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

const formatEventDate = (iso) => {
  if (!iso) return { day: '--', month: '---', label: '' };
  const d = new Date(iso);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  let prefix = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  if (sameDay(d, today)) prefix = 'Today';
  else if (sameDay(d, tomorrow)) prefix = 'Tomorrow';
  return {
    day: String(d.getDate()),
    month: months[d.getMonth()],
    label: `${prefix} · ${hh}:${mm}`,
  };
};

const formatRelativeTime = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
};

const isNew = (iso) => {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() < 24 * 60 * 60 * 1000;
};

// --- Care Packages (zone overview) helpers ---------------------------------

// Small rounded count pill used in the "Zone overview" row.
const OVERVIEW_TONE = {
  slate: 'bg-[#F1F5F9] dark:bg-[#31373f] text-[#475569] dark:text-gray-300',
  green: 'bg-[#ECFDF5] dark:bg-emerald-900/10 text-[#059669] dark:text-emerald-400',
  purple: 'bg-[#F5F3FF] dark:bg-violet-900/10 text-[#7C3AED] dark:text-violet-400',
  rose: 'bg-[#FFF1F3] dark:bg-red-900/10 text-[#E11D48] dark:text-red-400',
};
const OverviewChip = ({ label, tone = 'slate', dot = false }) => (
  <span className={`inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1 rounded-full ${OVERVIEW_TONE[tone]}`}>
    {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
    {label}
  </span>
);

// One of the three stat tiles inside a zone row (Visits today / Assigned / Unassigned).
const ZONE_STAT_TONE = {
  slate: 'bg-[#F4F7FC] dark:bg-[#31373f] text-[#0B1220] dark:text-gray-100',
  green: 'bg-[#ECFDF5] dark:bg-emerald-900/10 text-[#059669] dark:text-emerald-400',
  purple: 'bg-[#F5F3FF] dark:bg-violet-900/10 text-[#7C3AED] dark:text-violet-400',
  rose: 'bg-[#FFF1F3] dark:bg-red-900/10 text-[#E11D48] dark:text-red-400',
};
const ZoneStat = ({ value, label, tone }) => (
  <div className={`rounded-xl px-3 py-2.5 ${ZONE_STAT_TONE[tone]}`}>
    <div className="text-[20px] leading-none font-extrabold tabular-nums">{value}</div>
    <div className="text-[10.5px] font-medium text-[#94A3B8] dark:text-gray-500 mt-1.5">{label}</div>
  </div>
);

// One of the three top stat tiles. Same shell for both org types — only the
// icon / label / value / pill differ, so the markup lives in one place.
const StatCard = ({ icon: Icon, label, value, pill, onClick }) => (
  <div
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } }}
    className={`group relative overflow-hidden ${CARD} px-6 py-4 flex flex-col gap-3 cursor-pointer transition duration-200 hover:-translate-y-0.5 hover:shadow-[0_18px_40px_-12px_rgba(37,99,235,0.16)] hover:border-blue-300`}
    style={{ borderBottomColor: '#2563EB', borderBottomWidth: '3px' }}
  >
    <span className="pointer-events-none absolute -right-10 -top-10 w-[150px] h-[150px] rounded-full opacity-60 bg-[radial-gradient(circle,_rgba(37,99,235,0.10),_transparent_70%)]" />
    <div className="relative flex items-center gap-3 text-[13.5px] font-bold text-[#334155] dark:text-gray-200">
      <span className="w-10 h-10 rounded-xl grid place-items-center bg-gradient-to-br from-[#EEF3FF] to-white dark:from-blue-900/30 dark:to-[#262b33] text-[#2563EB] dark:text-blue-300 ring-1 ring-inset ring-[#D8E4FF] dark:ring-blue-900/50">
        <Icon className="w-5 h-5" />
      </span>
      {label}
    </div>
    <div className="relative flex items-end gap-3">
      <span className="text-[48px] font-extrabold text-[#0B1220] dark:text-gray-100 leading-[0.82] tracking-[-2px] tabular-nums">{value}</span>
      <span className="mb-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-lg bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400 border border-[#D8E4FF] dark:border-blue-900/50 whitespace-nowrap">
        {pill}
      </span>
    </div>
  </div>
);

// Today / Tomorrow segmented control for the Schedule / Care Packages card.
// Today is always the default; switching only swaps that one card.
const DAY_OPTIONS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
];
const DayToggle = ({ value, onChange, loading }) => (
  <div className="flex items-center gap-0.5 bg-[#F1F5F9] dark:bg-[#31373f] rounded-lg p-0.5">
    {DAY_OPTIONS.map(({ key, label }) => (
      <button
        key={key}
        onClick={() => onChange(key)}
        disabled={loading}
        className={`px-3 py-1.5 rounded-md text-[12px] font-bold transition disabled:opacity-60 ${value === key
          ? 'bg-white dark:bg-[#262b33] text-[#0B1220] dark:text-gray-100 shadow-sm'
          : 'text-[#64748B] dark:text-gray-400 hover:text-[#0B1220] dark:hover:text-gray-200'}`}
      >
        {label}
      </button>
    ))}
  </div>
);

// --- Schedule (care-home rota preview) helpers -----------------------------

// Team accent colours mirror the Rota grid's palette so a team reads as the
// same team on both screens.
const TEAM_COLORS = ['#2563eb', '#0bb77b', '#e3980f', '#6557d9', '#1287a5', '#e83652', '#728da8'];

// One carer chip inside a team row — avatar + name + shift window.
const StaffChip = ({ member, dayWord = 'today' }) => (
  <div className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 min-w-0 ${member.unavailable
    ? 'border-[#FFDCE3] dark:border-red-900/50 bg-[#FFF1F3] dark:bg-red-900/10'
    : 'border-[#EEF2F8] dark:border-gray-800 bg-white dark:bg-[#262b33]'}`}>
    <span className="w-7 h-7 shrink-0 rounded-lg grid place-items-center text-[10px] font-extrabold bg-[#EEF3FF] dark:bg-blue-900/30 text-[#2563EB] dark:text-blue-300">
      {initials(member.name)}
    </span>
    <div className="min-w-0">
      <div className="text-[12.5px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{member.name}</div>
      <div className="text-[10.5px] text-[#94A3B8] dark:text-gray-500 truncate">
        {member.unavailable
          ? `Unavailable ${dayWord}`
          : `${member.shift_start || '--:--'} – ${member.shift_end || '--:--'}${member.shift_name ? ` · ${member.shift_name}` : ''}`}
      </div>
    </div>
  </div>
);

// One team row: name + cover counts + up to two carers, the rest rolled into
// a "+N more" chip that opens the Rota.
const TeamRow = ({ team, color, onView, dayWord = 'today' }) => {
  const shown = team.staff.slice(0, 2);
  const extra = team.staff.length - shown.length;
  return (
    <div className="rounded-2xl border border-[#EEF2F8] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] p-4 flex flex-col lg:flex-row lg:items-center gap-4 hover:border-[#E3E9F3] transition">
      {/* name + manager */}
      <div className="flex items-center gap-2.5 w-full lg:w-[180px] shrink-0">
        <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: color }} />
        <div className="min-w-0">
          <div className="text-[13.5px] font-extrabold tracking-tight text-[#0B1220] dark:text-gray-100 uppercase truncate">{team.name}</div>
          <div className="text-[11px] text-[#94A3B8] dark:text-gray-500 truncate">
            {team.manager_name ? `Manager: ${team.manager_name}` : `${team.team_size} in team`}
          </div>
        </div>
      </div>

      {/* on-duty carers */}
      <div className="flex-1 min-w-0">
        {team.staff.length === 0 ? (
          <div className="text-[12px] text-[#94A3B8] dark:text-gray-500 rounded-xl border border-dashed border-[#E2E8F0] dark:border-gray-700 px-3 py-3">
            Nobody rostered {dayWord}.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {shown.map((m) => <StaffChip key={m.id} member={m} dayWord={dayWord} />)}
          </div>
        )}
        <div className="flex items-center gap-2 mt-2.5 flex-wrap">
          <OverviewChip label={`${team.on_duty} on duty`} tone="green" />
          {team.unavailable > 0 && <OverviewChip label={`${team.unavailable} unavailable`} tone="rose" dot />}
          {extra > 0 && (
            <button onClick={onView} className="text-[11.5px] font-bold text-blue-600 hover:text-blue-700">
              +{extra} more
            </button>
          )}
        </div>
      </div>

      {/* action */}
      <button
        onClick={onView}
        className="shrink-0 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-blue-600 hover:text-blue-700 border border-[#D8E4FF] dark:border-blue-900/50 rounded-lg px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
      >
        View rota <ArrowRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

const ZoneRow = ({ zone, onView }) => (
  <div className="rounded-2xl border border-[#EEF2F8] dark:border-gray-800 bg-[#FBFCFE] dark:bg-[#20242c] p-4 flex flex-col lg:flex-row lg:items-center gap-4 hover:border-[#E3E9F3] transition">
    {/* name + code */}
    <div className="flex items-center gap-2.5 w-full lg:w-[180px] shrink-0">
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: zone.color }} />
      <div className="min-w-0">
        <div className="text-[13.5px] font-extrabold tracking-tight text-[#0B1220] dark:text-gray-100 uppercase truncate">{zone.name}</div>
        <div className="text-[11px] text-[#94A3B8] dark:text-gray-500 truncate">{zone.code || '—'}</div>
      </div>
    </div>

    {/* stats + coverage */}
    <div className="flex-1 min-w-0">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <ZoneStat value={zone.visits} label="Visits today" tone="slate" />
        <ZoneStat value={zone.assigned} label="Assigned" tone="green" />
        <ZoneStat value={zone.unassigned} label="Unassigned" tone="purple" />
        <ZoneStat value={zone.toReview} label="Review" tone="rose" />
      </div>
      <div className="flex items-center gap-3 mt-2.5">
        <span className="text-[11px] text-[#94A3B8] dark:text-gray-500 shrink-0">Assignment coverage</span>
        <div className="flex-1 h-1.5 rounded-full bg-[#EEF2F7] dark:bg-gray-800 overflow-hidden">
          <div className="h-full rounded-full bg-emerald-500" style={{ width: `${zone.coverage}%` }} />
        </div>
        <span className="text-[11px] font-semibold text-[#64748B] dark:text-gray-400 shrink-0 tabular-nums">{zone.coverage}% assigned</span>
      </div>
    </div>

    {/* action */}
    <button
      onClick={onView}
      className="shrink-0 inline-flex items-center gap-1.5 text-[12.5px] font-bold text-blue-600 hover:text-blue-700 border border-[#D8E4FF] dark:border-blue-900/50 rounded-lg px-3 py-2 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition"
    >
      View zone <ArrowRight className="w-3.5 h-3.5" />
    </button>
  </div>
);

/**
 * Staff reports at a glance — the open ones, urgent first, from the same API as
 * the Staff Reports page (so a maintenance-only user sees only maintenance
 * jobs, titled so). Hidden for anyone without access to staff reports.
 */
const STAFF_REPORT_STATUS = {
  new: { label: 'Open', cls: 'bg-[#EEF3FF] text-[#2563EB] dark:bg-blue-900/20 dark:text-blue-400' },
  in_review: { label: 'In progress', cls: 'bg-[#FFFBEB] text-[#D97706] dark:bg-amber-900/10 dark:text-amber-400' },
  actioned: { label: 'Resolved', cls: 'bg-[#ECFDF5] text-[#059669] dark:bg-emerald-900/10 dark:text-emerald-400' },
};
const StaffReportsCard = ({ onOpen }) => {
  const [state, setState] = useState({ loading: true, hidden: false, reports: [], stats: {}, scope: 'all' });
  useEffect(() => {
    let live = true;
    staffReportsService.list({ status: 'all' })
      .then((res) => {
        if (!live) return;
        const open = (res.data || []).filter((r) => r.status !== 'closed');
        setState({ loading: false, hidden: !res.success, reports: open.slice(0, 4), stats: res.stats || {}, scope: res.scope || 'all' });
      })
      .catch(() => { if (live) setState((s) => ({ ...s, loading: false, hidden: true })); });
    return () => { live = false; };
  }, []);
  if (state.hidden) return null;
  const maintenanceOnly = state.scope === 'maintenance';
  return (
    <div className={CARD}>
      <div className="flex items-center justify-between px-[22px] pt-[18px] pb-1.5">
        <h3 className="flex items-center gap-[11px] text-[15px] font-bold text-[#0B1220] dark:text-gray-100">
          <Spine from="from-amber-500" to="to-orange-600" />{maintenanceOnly ? 'Maintenance jobs' : 'Staff reports'}
          {state.stats.urgent > 0 && (
            <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-[#FFF1F3] dark:bg-red-900/10 text-[#E11D48]">
              {state.stats.urgent} urgent
            </span>
          )}
        </h3>
        <button onClick={onOpen} className="text-[12.5px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1">
          See all <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>
      {state.loading ? (
        <p className="text-[12px] text-[#94A3B8] px-[22px] pb-5">Loading…</p>
      ) : !state.reports.length ? (
        <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 italic px-[22px] pb-5">
          {maintenanceOnly ? 'No open maintenance jobs.' : 'No open staff reports.'}
        </p>
      ) : (
        <div>
          {state.reports.map((r) => (
            <div key={r.id} onClick={onOpen} className="px-[22px] py-3 border-t border-[#EEF2F8] dark:border-gray-800 cursor-pointer group">
              <div className="flex items-start justify-between gap-2">
                <p className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 leading-tight line-clamp-2 group-hover:text-blue-600">{r.summary}</p>
                <span className={`shrink-0 text-[10px] font-bold px-2 py-0.5 rounded-full ${STAFF_REPORT_STATUS[r.status]?.cls || ''}`}>
                  {STAFF_REPORT_STATUS[r.status]?.label || r.statusLabel}
                </span>
              </div>
              <p className="text-[10.5px] text-[#94A3B8] dark:text-gray-500 mt-1 flex flex-wrap items-center gap-[7px]">
                {r.priority === 'urgent' && <span className="font-extrabold uppercase text-[#E11D48]">Urgent</span>}
                <span>{r.reference}</span><span>·</span><span>{r.category}</span>
                {r.maintenance && !maintenanceOnly && <><span>·</span><span className="font-semibold text-[#B45309]">Maintenance</span></>}
                <span>·</span><span>{formatRelativeTime(r.submittedAt)}</span>
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isDomiciliary = user?.organization?.organization_type === 'domiciliary';
  // The site's own name in a multi-site group (site_label), else the organisation's.
  const orgName = user?.organization?.site_label || user?.organization?.organization_name || user?.organization?.name || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [announcementModal, setAnnouncementModal] = useState(false);
  const [announcementForm, setAnnouncementForm] = useState({ title: '', body: '' });
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  // Day-scoped card (Care Packages / Schedule). Today comes free with the main
  // dashboard call; tomorrow is fetched on demand and then cached, so toggling
  // back and forth doesn't re-hit the API.
  const [dayView, setDayView] = useState('today');
  const [daySections, setDaySections] = useState({});
  const [dayLoading, setDayLoading] = useState(false);

  const fetchDashboard = async () => {
    try {
      const res = await dashboardService.getDashboard();
      if (res.success) {
        setData(res.data);
        // Seed today's section from the same payload and drop any cached
        // tomorrow, which may now be stale.
        setDaySections({
          today: {
            date: res.data.today,
            zoneOverview: res.data.zoneOverview,
            schedule: res.data.schedule,
            visits: res.data.stats.visits,
          },
        });
      }
    } catch (err) {
      console.error('Dashboard fetch error', err);
      toast.error('Could not load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const selectDay = async (key) => {
    setDayView(key);
    if (daySections[key]) return;
    try {
      setDayLoading(true);
      const res = await dashboardService.getSchedule(key);
      if (res.success) setDaySections((prev) => ({ ...prev, [key]: res.data }));
    } catch (err) {
      toast.error("Could not load tomorrow's schedule");
      setDayView('today');
    } finally {
      setDayLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateAnnouncement = async () => {
    if (!announcementForm.title.trim()) {
      toast.error('Title is required');
      return;
    }
    try {
      setAnnouncementSaving(true);
      const res = await announcementService.create(announcementForm);
      if (res.success) {
        toast.success('Announcement posted');
        setAnnouncementForm({ title: '', body: '' });
        setAnnouncementModal(false);
        fetchDashboard();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to post');
    } finally {
      setAnnouncementSaving(false);
    }
  };

  if (loading) return <DashboardSkeleton />;
  if (!data) return null;

  const clients = data.stats.clients;
  const staff = data.stats.staff;
  const visits = data.stats.visits;
  // Care-home only (null for domiciliary orgs).
  const occupancy = data.stats.occupancy;
  // The active day's card data — today's arrives with the dashboard payload,
  // tomorrow's from /dashboard/schedule.
  const activeDay = daySections[dayView] || {};
  // Per-zone "Care Packages" overview — visits/assigned/unassigned come from
  // the backend (null for care-home orgs). "To review" is derived here with the
  // schedule screen's own visitReviewKind connector over the raw per-visit
  // fields the backend ships, so both screens always agree.
  const rawZoneOverview = activeDay.zoneOverview;
  const reviewCountFor = (z) => (z.reviewVisits || []).filter((v) => visitReviewKind(v)).length;
  const zoneOverview = rawZoneOverview
    ? (() => {
        const zonesWithReview = rawZoneOverview.zones.map((z) => ({ ...z, toReview: reviewCountFor(z) }));
        return {
          ...rawZoneOverview,
          zones: zonesWithReview,
          totals: {
            ...rawZoneOverview.totals,
            toReview: zonesWithReview.reduce((sum, z) => sum + z.toReview, 0),
          },
        };
      })()
    : null;
  const schedule = activeDay.schedule;
  // Wording that follows the toggle, so counts are never mislabelled "today".
  const dayWord = dayView === 'today' ? 'today' : 'tomorrow';
  const newAnnouncements = data.announcements.filter((a) => isNew(a.created_at)).length;

  return (
    <div className="w-full pb-10 flex flex-col gap-5 -mt-2 md:-mt-4">

      {/* Page header */}
      <div>
        <h1 className="text-[26px] font-extrabold text-[#0B1220] dark:text-gray-100 tracking-[-0.8px] leading-none">Dashboard</h1>
        <p className="mt-1.5 text-[13.5px] text-[#64748B] dark:text-gray-400">
          {orgName ? `${orgName} · ` : ''}daily operations at a glance.
        </p>
      </div>

      {/* Main grid — left (stats + care packages) / right rail (top-aligned) */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_360px] gap-[22px] items-start">

        {/* LEFT — stat row + Care Packages (domiciliary) / Schedule (care home) */}
        <div className="flex flex-col gap-[22px] min-w-0">

          {/* Stat row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-[22px]">
            {isDomiciliary ? (
              <>
                <StatCard
                  icon={Users}
                  label="Total Clients"
                  value={clients.total}
                  pill="Live"
                  onClick={() => navigate('/manager/residences')}
                />
                <StatCard
                  icon={Users}
                  label="Total Staff"
                  value={staff.total}
                  pill="Available"
                  onClick={() => navigate('/manager/staffs')}
                />
                <StatCard
                  icon={Clock}
                  label="Today's Visits"
                  value={visits.total}
                  pill={`${visits.completed} of ${visits.total} done`}
                  onClick={() => navigate('/manager/domiciliary-schedule')}
                />
              </>
            ) : (
              /* Care home: residents / cover / beds — the three numbers a
                 care-home manager reads first thing in the morning. */
              <>
                <StatCard
                  icon={Users}
                  label="Total Residents"
                  value={clients.total}
                  pill="Active"
                  onClick={() => navigate('/manager/residences')}
                />
                <StatCard
                  icon={UserCheck}
                  label="Staff on Duty"
                  value={staff.onDutyToday ?? 0}
                  pill="On duty"
                  onClick={() => navigate('/manager/duty-roster')}
                />
                <StatCard
                  icon={BedDouble}
                  label="Occupancy"
                  value={occupancy?.capacity ? `${occupancy.occupied}/${occupancy.capacity}` : occupancy?.occupied ?? 0}
                  pill={occupancy?.capacity ? `${occupancy.free} beds free` : 'No capacity set'}
                  onClick={() => navigate('/manager/residences')}
                />
              </>
            )}
          </div>

          {/* Today's Visits (domiciliary) or Quick links (care-home) */}
          {isDomiciliary ? (
          <div className={CARD}>
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap px-[22px] pt-[18px] pb-3 border-b border-[#EEF2F8] dark:border-gray-800">
              <h3 className="flex items-center gap-[11px] text-[15.5px] font-bold text-[#0B1220] dark:text-gray-100">
                <Spine />Care Packages
                {dayLoading && <Spinner size="h-3.5 w-3.5" className="text-[#94A3B8]" />}
              </h3>
              <DayToggle value={dayView} onChange={selectDay} loading={dayLoading} />
            </div>

            {/* Zone overview chips */}
            <div className="flex items-center flex-wrap gap-2 px-[22px] py-3">
              <span className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mr-1">Zone overview</span>
              <OverviewChip label={`${zoneOverview?.totals.visits ?? 0} visits`} tone="slate" />
              <OverviewChip label={`${zoneOverview?.totals.assigned ?? 0} assigned`} tone="green" />
              <OverviewChip label={`${zoneOverview?.totals.unassigned ?? 0} unassigned`} tone="purple" />
              {(zoneOverview?.totals.toReview ?? 0) > 0 && (
                <OverviewChip label={`${zoneOverview.totals.toReview} to review`} tone="rose" dot />
              )}
            </div>

            {/* Zone rows */}
            {!zoneOverview || zoneOverview.zones.length === 0 ? (
              <div className="px-[22px] pb-6">
                <div className="text-[13px] text-[#64748B] dark:text-gray-400 bg-[#F8FAFC] dark:bg-[#20242c] border border-dashed border-[#E2E8F0] dark:border-gray-700 rounded-xl px-4 py-8 text-center">
                  <MapPin className="w-5 h-5 mx-auto mb-2 text-[#94A3B8]" />
                  No zones set up yet. Create zones in the schedule to see {dayWord}'s coverage here.
                </div>
              </div>
            ) : (
              <div className="px-[22px] pb-2 space-y-3">
                {zoneOverview.zones.map((z) => (
                  <ZoneRow
                    key={z.id}
                    zone={z}
                    onView={() => navigate('/manager/domiciliary-schedule', { state: { zoneId: z.id } })}
                  />
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between px-[22px] py-3 mt-1 border-t border-[#EEF2F8] dark:border-gray-800">
              <span className="text-[12px] text-[#64748B] dark:text-gray-400">
                {zoneOverview?.zones.length ?? 0} zones · {zoneOverview?.totals.visits ?? 0} visits {dayWord}
              </span>
              <button
                onClick={() => navigate('/manager/domiciliary-schedule')}
                className="text-[12.5px] font-bold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
              >
                View all zones <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ) : (
          /* Care home: rota preview — the counterpart to the domiciliary
             "Care Packages" widget. */
          <div className={CARD}>
            {/* Header */}
            <div className="flex items-center justify-between gap-3 flex-wrap px-[22px] pt-[18px] pb-3 border-b border-[#EEF2F8] dark:border-gray-800">
              <h3 className="flex items-center gap-[11px] text-[15.5px] font-bold text-[#0B1220] dark:text-gray-100">
                <Spine />Schedule
                {schedule?.day && (
                  <span className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400">{schedule.day}</span>
                )}
                {dayLoading && <Spinner size="h-3.5 w-3.5" className="text-[#94A3B8]" />}
              </h3>
              <DayToggle value={dayView} onChange={selectDay} loading={dayLoading} />
            </div>

            {/* Cover summary chips */}
            <div className="flex items-center flex-wrap gap-2 px-[22px] py-3">
              <span className="text-[12px] font-semibold text-[#64748B] dark:text-gray-400 mr-1">
                Cover {dayWord}
              </span>
              <OverviewChip label={`${schedule?.totals.teams ?? 0} teams`} tone="slate" />
              <OverviewChip label={`${schedule?.totals.onDuty ?? 0} on duty`} tone="green" />
              {(schedule?.totals.rostered ?? 0) - (schedule?.totals.onDuty ?? 0) > 0 && (
                <OverviewChip
                  label={`${schedule.totals.rostered - schedule.totals.onDuty} unavailable`}
                  tone="rose"
                  dot
                />
              )}
            </div>

            {/* Team rows */}
            {!schedule || schedule.teams.length === 0 ? (
              <div className="px-[22px] pb-6">
                <div className="text-[13px] text-[#64748B] dark:text-gray-400 bg-[#F8FAFC] dark:bg-[#20242c] border border-dashed border-[#E2E8F0] dark:border-gray-700 rounded-xl px-4 py-8 text-center">
                  <Users className="w-5 h-5 mx-auto mb-2 text-[#94A3B8]" />
                  No teams set up yet. Add a team in the Rota to see {dayWord}'s cover here.
                </div>
              </div>
            ) : (
              <div className="px-[22px] pb-2 space-y-3">
                {schedule.teams.map((t, i) => (
                  <TeamRow
                    key={t.id}
                    team={t}
                    dayWord={dayWord}
                    color={TEAM_COLORS[i % TEAM_COLORS.length]}
                    onView={() => navigate('/manager/duty-roster')}
                  />
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="flex items-center justify-between px-[22px] py-3 mt-1 border-t border-[#EEF2F8] dark:border-gray-800">
              <span className="text-[12px] text-[#64748B] dark:text-gray-400">
                {schedule?.totals.teams ?? 0} teams · {schedule?.totals.onDuty ?? 0} on duty {dayWord}
              </span>
              <button
                onClick={() => navigate('/manager/duty-roster')}
                className="text-[12.5px] font-bold text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
              >
                View all <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        )}
        </div>

        {/* RIGHT — rail */}
        <div className="flex flex-col gap-[22px]">

          {/* Reminders (shared functional widget — Add / complete / View all) */}
          <RemindersCard reminders={(data.reminders || []).slice(0, 3)} onChanged={fetchDashboard} />

          {/* Staff reports (or, for the maintenance team, their open jobs). */}
          <StaffReportsCard onOpen={() => navigate('/manager/staffs-report')} />

          {/* Announcements */}
          <div className={CARD}>
            <div className="flex items-center justify-between px-[22px] pt-[18px] pb-1.5">
              <h3 className="flex items-center gap-[11px] text-[15px] font-bold text-[#0B1220] dark:text-gray-100">
                <Spine />Announcements
                {newAnnouncements > 0 && (
                  <span className="text-[10.5px] font-bold px-2 py-0.5 rounded-full bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400">
                    {newAnnouncements} New
                  </span>
                )}
              </h3>
              <button
                onClick={() => navigate('/manager/announcements')}
                className="flex items-center gap-1 text-[12.5px] font-bold text-blue-600 hover:text-blue-700"
              >
                <Plus className="w-3.5 h-3.5" /> Post
              </button>
            </div>

            {data.announcements.length === 0 ? (
              <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 italic px-[22px] pb-5">No announcements yet.</p>
            ) : (
              <div>
                {data.announcements.slice(0, 3).map((a) => (
                  <div
                    key={a.id}
                    onClick={() => navigate('/manager/announcements')}
                    className="px-[22px] py-3 border-t border-[#EEF2F8] dark:border-gray-800 cursor-pointer group"
                  >
                    <div className="border-l-2 border-blue-500 pl-[13px] group-hover:border-blue-600 transition-colors">
                      <p className="text-[13px] font-bold text-[#0B1220] dark:text-gray-100 leading-tight">{a.title}</p>
                      {a.body && (
                        <p className="text-[11.5px] text-[#64748B] dark:text-gray-400 leading-[1.5] mt-1 line-clamp-2">{a.body}</p>
                      )}
                      <p className="text-[10.5px] text-[#94A3B8] dark:text-gray-500 mt-1.5 flex items-center gap-[7px]">
                        {a.creator_name && <span className="font-semibold text-[#475569] dark:text-gray-400">{a.creator_name}</span>}
                        {a.creator_name && <span>·</span>}
                        <span>{formatRelativeTime(a.created_at)}</span>
                        {isNew(a.created_at) && (
                          <span className="text-[8.5px] font-extrabold uppercase tracking-wide px-1.5 py-0.5 rounded bg-[#EEF3FF] dark:bg-blue-900/20 text-[#2563EB] dark:text-blue-400">New</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Upcoming Events */}
          <div className={CARD}>
            <div className="flex items-center justify-between px-[22px] pt-[18px] pb-1.5">
              <h3 className="flex items-center gap-[11px] text-[15px] font-bold text-[#0B1220] dark:text-gray-100">
                <Spine from="from-rose-500" to="to-rose-600" />Upcoming Events
              </h3>
              <button
                onClick={() => navigate('/manager/events')}
                className="text-[12.5px] font-bold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                See all <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>

            {data.events.length === 0 ? (
              <p className="text-[12px] text-[#94A3B8] dark:text-gray-500 italic px-[22px] pb-5">No upcoming events.</p>
            ) : (
              <div>
                {data.events.slice(0, 3).map((e) => {
                  const dt = formatEventDate(e.event_datetime);
                  return (
                    <div key={e.id} className="flex items-center gap-[13px] px-[22px] py-3 border-t border-[#EEF2F8] dark:border-gray-800">
                      <div className="w-[46px] h-[46px] rounded-[13px] bg-[#FFF1F3] dark:bg-red-900/10 ring-1 ring-inset ring-[#FFDCE3] dark:ring-red-900/50 flex flex-col items-center justify-center shrink-0">
                        <span className="text-[17px] font-extrabold text-[#E11D48] leading-none tabular-nums">{dt.day}</span>
                        <span className="text-[9px] font-bold text-[#E11D48] tracking-tight">{dt.month}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13.5px] font-bold text-[#0B1220] dark:text-gray-100 truncate">{e.event_name}</p>
                        <p className="text-[11px] text-[#94A3B8] dark:text-gray-500 mt-0.5 flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" />
                          {dt.label}
                          {e.creator_name && <span>· {e.creator_name}</span>}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Announcement modal (unchanged) */}
      <Modal
        isOpen={announcementModal}
        onClose={() => setAnnouncementModal(false)}
        title={<span className="flex items-center gap-2"><Megaphone className="w-5 h-5 text-[#2563EB]" />Post Announcement</span>}
        maxWidth="max-w-[500px]"
        footer={
          <div className="flex gap-2">
            <button
              onClick={() => setAnnouncementModal(false)}
              className="flex-1 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 text-[13px] font-bold text-[#475569] dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#31373f]"
            >
              Cancel
            </button>
            <button
              onClick={handleCreateAnnouncement}
              disabled={announcementSaving}
              className="flex-1 py-2.5 rounded-xl bg-[#2563EB] text-white text-[13px] font-bold hover:bg-blue-700 transition disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {announcementSaving && <Spinner />}
              {announcementSaving ? 'Posting…' : 'Post Announcement'}
            </button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-[11px] font-bold text-[#475569] dark:text-gray-400 uppercase tracking-wider mb-1.5">Title</label>
            <input
              type="text"
              value={announcementForm.title}
              onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
              placeholder="e.g. Updated medication policy"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[13px] font-medium"
            />
          </div>
          <div>
            <label className="block text-[11px] font-bold text-[#475569] dark:text-gray-400 uppercase tracking-wider mb-1.5">Details (optional)</label>
            <textarea
              rows={4}
              value={announcementForm.body}
              onChange={(e) => setAnnouncementForm({ ...announcementForm, body: e.target.value })}
              placeholder="What's the announcement about?"
              className="w-full px-3 py-2.5 rounded-xl border border-[#E2E8F0] dark:border-gray-700 dark:bg-[#31373f] dark:text-gray-200 focus:border-blue-500 outline-none text-[12px] resize-none"
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Dashboard;
